
import React, { useState, useContext, useMemo } from "react";
import { Button, Stack, Typography, Box, Container, CircularProgress, Divider, Tooltip } from "@mui/material";
import { CheckCircle, VerifiedUser, Lock, ArrowForward } from "@mui/icons-material";
import { usePopup } from "src/contexts/popupContext";
import PhoneImage from "../../../images/Access-app.webp";
import { AuthDialogContext } from "../AuthDialog";
import QuickLogin from "src/components/quickLogin/quickLogin";
import { motion, AnimatePresence } from "framer-motion";



function AccessLogin({ closeDialog }) {
  const { openSignupDialog } = useContext(AuthDialogContext);
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const reduceMotion = useMemo(() => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);

  const handleLoginSuccess = () => {
    setLoading(false);
    setLoginSuccess(true);
    // Remove full page reload; rely on context state + SPA routing.
    // If you need to close the dialog after success:
    setTimeout(() => {
      closeDialog && closeDialog();
    }, 1500);
  };

  // Premium glass/gradient background
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 60% 10%, #182c2b 0%, #10181f 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 6,
      px: 2,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated emerald/gold light band */}
      {!reduceMotion && (
        <motion.div
          initial={{ opacity: 0, y: -80 }}
          animate={{ opacity: [0, 0.22, 0.12, 0], y: [ -80, 0, 40, 120 ] }}
          transition={{ duration: 7.5, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut', delay: 0.8 }}
          style={{
            position: 'absolute',
            left: '-20%',
            top: 0,
            width: '140%',
            height: 180,
            background: 'linear-gradient(90deg, #14B88D 0%, #F5C84C 100%)',
            filter: 'blur(32px)',
            opacity: 0.18,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}
      <Container maxWidth={false} disableGutters sx={{
        borderRadius: 5,
        boxShadow: '0 8px 48px 0 #14B88D33, 0 0 0 2px #F5C84C22',
        background: 'linear-gradient(120deg, #10181f 60%, #182c2b 100%)',
        p: { xs: 2.5, md: 4 },
        position: 'relative',
        zIndex: 2,
        overflow: 'hidden',
        minWidth: { xs: 320, md: 600 },
        maxWidth: 720,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 3, md: 6 },
      }}>
        {/* Left: Trust narrative, QR login, actions */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', py: { xs: 1, md: 3 }, px: { xs: 0, md: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <VerifiedUser sx={{ color: '#14B88D', fontSize: 28, mr: 0.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#F5C84C', letterSpacing: 1.2, textShadow: '0 2px 12px #052C2288' }}>
              Neuro Exchange Brasil
            </Typography>
          </Stack>
          <Typography variant="subtitle1" sx={{ color: '#e8faf6', fontWeight: 600, mb: 1, textAlign: 'left', letterSpacing: 0.2 }}>
            Entrar com Neuro Access<br />
            <span style={{ color: '#14B88D', fontWeight: 700 }}>Segurança, privacidade e confiança</span>
          </Typography>
          <Typography variant="body2" sx={{ color: '#b6e2d6', mb: 2, textAlign: 'left', fontWeight: 400 }}>
            Acesse sua conta com QR Code ou app. Nenhuma senha é armazenada.<br />
            <span style={{ color: '#F5C84C', fontWeight: 500 }}>100% brasileiro, 100% seguro.</span>
          </Typography>
          <Divider sx={{ my: 2, borderColor: '#14B88D', opacity: 0.22, width: '100%' }} />
          <Box sx={{
            background: 'linear-gradient(120deg,rgba(20,184,141,0.10) 0%,rgba(245,200,76,0.10) 100%)',
            borderRadius: 3,
            p: 2.2,
            mb: 2.5,
            boxShadow: '0 2px 12px 0 #14B88D22',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 0,
            width: '100%',
            maxWidth: 340,
          }}>
            <QuickLogin
              neuron={window.location.host}
              purpose={"Login para Neuro Exchange"}
              active={true}
              onLoginSuccess={handleLoginSuccess}
            />
          </Box>
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                transition={{ duration: 0.4 }}
                style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}
              >
                <CircularProgress size={38} sx={{ color: '#14B88D', mb: 1 }} />
                <Typography variant="subtitle1" sx={{ color: '#e8faf6', fontWeight: 600 }}>
                  Entrando... Aguarde
                </Typography>
              </motion.div>
            )}
            {loginSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.5 }}
                style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}
              >
                <CheckCircle sx={{ color: '#14B88D', fontSize: 38, mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#F5C84C', fontWeight: 800 }}>
                  Login realizado com sucesso!
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
          {!loading && !loginSuccess && (
            <Stack direction="row" spacing={2} sx={{ mt: 1.5, width: '100%' }}>
              <Button
                variant="contained"
                onClick={openSignupDialog}
                sx={{
                  background: 'linear-gradient(90deg,#14B88D 0%,#F5C84C 100%)',
                  color: '#052C22',
                  fontWeight: 900,
                  letterSpacing: 1.2,
                  py: 1.2,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px -2px rgba(20,184,141,0.45)',
                  '&:hover': { background: 'linear-gradient(90deg,#13a57e 0%,#e5b647 100%)' },
                  flex: 1,
                  fontSize: 17,
                }}
                endIcon={<ArrowForward sx={{ fontSize: 22, ml: 0.5 }} />}
              >
                Criar Conta
              </Button>
              <Button
                variant="outlined"
                onClick={closeDialog}
                sx={{
                  borderColor: '#14B88D',
                  color: '#e8faf6',
                  fontWeight: 700,
                  letterSpacing: 1.1,
                  py: 1.2,
                  borderRadius: 2,
                  flex: 1,
                  fontSize: 17,
                  '&:hover': { borderColor: '#F5C84C', color: '#F5C84C', background: 'rgba(245,200,76,0.04)' },
                }}
              >
                Cancelar
              </Button>
            </Stack>
          )}
          <Divider sx={{ my: 3, borderColor: '#F5C84C', opacity: 0.13, width: '100%' }} />
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ mb: 0.5 }}>
            <Lock sx={{ color: '#14B88D', fontSize: 20, mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: '#b6e2d6', fontWeight: 500, letterSpacing: 0.2 }}>
              Seus dados nunca são compartilhados. Proteção máxima, tecnologia brasileira.
            </Typography>
          </Stack>
        </Box>
        {/* Right: Device preview with caption */}
        <Box sx={{ flex: '0 0 220px', minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: { xs: 2, md: 0 } }}>
          <Box sx={{ position: 'relative', width: 160, height: 320, borderRadius: 8, overflow: 'hidden', boxShadow: '0 6px 40px 0 #14B88D33, 0 0 0 2px #F5C84C22', mb: 1.5, background: 'rgba(20,184,141,0.07)' }}>
            <img src={PhoneImage} alt="App Neuro-Access: tela de login" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16, filter: 'brightness(1) contrast(1.12)' }} loading="lazy" />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg,rgba(20,184,141,0.10) 0%,rgba(245,200,76,0.10) 100%)', pointerEvents: 'none' }} />
          </Box>
          <Typography variant="caption" sx={{ color: '#e8faf6', textAlign: 'center', fontWeight: 500, letterSpacing: 0.1, px: 1.5 }}>
            Use o app <span style={{ color: '#14B88D', fontWeight: 700 }}>Neuro-Access</span> para entrar
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default AccessLogin;
